import {booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output} from "@angular/core";
import {MatIcon} from "@angular/material/icon";
import {AvstemningsstatusModell, GruppeId, MøtedeltakerModell, MøteId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TranslateModule} from "@ngx-translate/core";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIconButton} from "@angular/material/button";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {FormaterProsentPipe} from "felles-ng2/pipes/FormaterProsentPipe";
import {C3TodeltPaiKomponent, C3TodeltPaiKonfig, TooltipTekst} from "web-ng2/delt/komponenter/c3-todelt-pai/c3-todelt-pai";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {Bekreftelsesdialog} from "web-ng2/tjenester/Bekreftelsesdialog";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {totaltAntallAksjerForMøtedeltaker} from "felles-ng2/modell/møtedeltakerModellHjelpere";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {farge_grå_300_styggedalstind, farge_profil_500_bergen, farge_suksess_200_hoved} from "felles-ng2/fargepalett";

const debug = initDebugger('AvstemningVisStemmeberettigedeKomponent');

@Component({
    selector: 'avstemning-vis-stemmeberettigede',
    templateUrl: 'avstemning-vis-stemmeberettigede.html',
    styleUrls: ['avstemning-vis-stemmeberettigede.scss'],
    imports: [
        MatTooltip,
        MatMenu,
        MatMenuTrigger,
        MatMenuItem,
        MatIcon,
        MatIconButton,
        TranslateModule,
        FormaterProsentPipe,
        C3TodeltPaiKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvstemningVisStemmeberettigedeKomponent {
    static ajsSelector = 'moeteStemmeberettigede';

    vedNullstillAvgitteStemmer = output<MøtedeltakerModell>();

    moeteId = input.required<MøteId>();
    gruppeId = input.required<GruppeId>();
    avstemningsstatus = input.required<AvstemningsstatusModell>();
    moetedeltakere = input.required<MøtedeltakerModell[]>();
    visAntallAksjer = input(false, {transform: booleanAttribute});

    modeller = computed(() => this.deltakererSomStemmeberettigetModeller());
    pieKonfig: Partial<C3TodeltPaiKonfig> = {
        ikkePåbegyntPieBorderFarge: farge_grå_300_styggedalstind,
        ikkePåbegyntFarge: '#ffffff00', // transparent farge gir riktig hover-effekt inni md-button
        andelFarge: farge_profil_500_bergen,
        fullførtFarge: farge_suksess_200_hoved
    }

    constructor(private navigasjon: Navigasjon, private oversettelser: Oversettelser, private bekreftelsesdialog: Bekreftelsesdialog) {
    }

    pieTooltip(): TooltipTekst {
        return (kolonneId: string, antall: number) => {
            const totalt = this.avstemningsstatus().antallAvstemninger;
            return kolonneId == 'andel' ?
              this.oversettelser.oversett(i18n('Stemt på {antall} av {totalt} avstemninger'), {antall: antall, totalt: totalt}) :
              this.oversettelser.oversett(i18n('Ikke stemt på {antall} av {totalt} avstemninger'), {antall: antall, totalt: totalt});
        }
    }

    gaaTilMedlem(stemmeberettiget: StemmeberettigetModell) {
        this.navigasjon.gåTilMedlem(this.gruppeId(), stemmeberettiget.deltaker.medlem.id);
    }

    async nullstillAvgitteStemmer(modell: StemmeberettigetModell) {
        const nullstill = await this.bekreftelsesdialog.visFare({
            tittel: this.oversettelser.oversett(i18n('Nullstill stemmegivning')),
            innhold: this.oversettelser.oversett(i18n('Bekreft at du vil slette svarene til {navn}. Handlingen kan ikke angres.'), {navn: modell.deltaker.medlem.navn}),
            okKnapp: this.oversettelser.oversett(i18n('Nullstill'))
        }).somPromise()
        if (nullstill) this.vedNullstillAvgitteStemmer.emit(modell.deltaker);
    }

    // private hjelpemetoder

    private deltakererSomStemmeberettigetModeller(): StemmeberettigetModell[] {
        const antallAksjerGrandTotal = første(this.moetedeltakere()
          .flatMap(deltaker => deltaker.aksjeeierRepresentasjoner)
          .map(aksjeeier => aksjeeier.totalBeholdning)) ?? 0;
        return this.moetedeltakere().map(deltaker => {
            const antallAksjerTotalt = totaltAntallAksjerForMøtedeltaker(deltaker);
            const eierandel = Math.floor((antallAksjerTotalt / antallAksjerGrandTotal) * 10000) / 100;
            const advarslerTooltipTekst = antallAksjerTotalt != deltaker.stemmevekt ? this.oversettelser.oversett(i18n('Stemmevekt ({vekt}) er ulik antall aksjer ({antall}), rediger for å rette på dette.'), {vekt: deltaker.stemmevekt, antall: antallAksjerTotalt}) : null;
            const pieTooltipTekst = this.oversettelser.oversett(i18n('Stemt på {antall} av {totalt} avstemninger'), {antall: this.avstemningsstatus().antallStemmerAvgittPerDeltaker[deltaker.id], totalt: this.avstemningsstatus().antallAvstemninger});
            return {deltaker, antallAksjerTotalt, eierandel, advarslerTooltipTekst, pieTooltipTekst};
        })
    }
}

interface StemmeberettigetModell {
    deltaker: MøtedeltakerModell;
    antallAksjerTotalt: number;
    eierandel: number;
    advarslerTooltipTekst: string | null;
    pieTooltipTekst: string | null;
}
